'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { AnimationItem } from 'lottie-web';
import { useLottieAnimation } from 'hooks/animation/useLottieAnimation';
import styles from './index.module.css';

/* Zap.json timeline (120f @ 30fps): stroke draw-in 0–30, matte wipe 29–34,
 * flicker through ~50, hold through ~114.999, fly-off 115–120. Park on the
 * rest frame for first paint; each play is the full [0, 60] action (never
 * 115+ or the icon blanks out). resetSegments before play — initialSegment
 * clamps the timeline to the rest frame until we release it. */
const ZAP_LOTTIE_PATH = '/lottie/Zap.json';
const ZAP_REST_FRAME = 100;
const ZAP_PLAY_SEGMENT: [number, number] = [0, 60];
const ZAP_PLAY_SPEED = 1;
const ZAP_INTRO_DEFER_MS = 70;

/** lottie-web paints frame 0 synchronously on load and Zap.json's frame 0 is
 * blank (the bolt's track matte starts off-canvas), so initialise directly
 * on the rest frame. Module-level: a fresh array per render would tear the
 * animation down (it is in useLottieAnimation's dependency list). */
const ZAP_INITIAL_SEGMENT: [number, number] = [
  ZAP_REST_FRAME,
  ZAP_REST_FRAME + 1,
];

/** Same silhouette as Zap.json's filled Vector layer (rest frame). */
const StaticZapMark = () => (
  <svg viewBox="-280 -380 560 760" fill="currentColor" aria-hidden>
    <path d="M33.125-129.136 266.943-129.098-30.217 255.118-120.193 369.262-16.022 25.881-266.943 25.823-86.049-368.86 156.036-368.835Z" />
  </svg>
);

/**
 * 24px Zap in the title row. Reduced motion never loads lottie-web (static
 * mark only). Otherwise: park on the rest frame, play once on first real
 * visibility (70ms defer, then drop the observer), and again only when
 * `playToken` increments. No wall-clock replays.
 *
 * Shared between India's DeliveryEtaBadge (components/ring-air/DeliveryEtaBadge)
 * and the UAE delivery-promise card (components/ring-air/AeDeliveryEta) — both
 * need the identical 24px icon, intro animation, and prefers-reduced-motion
 * handling. India bumps `playToken` to replay on state transitions (eligible
 * result, Change); the AE card never changes state post-mount, so it passes a
 * constant token and only ever gets the one-time intro play.
 */
export const ZapIcon = ({ playToken }: { playToken: number }) => {
  const [motionOk, setMotionOk] = useState(false);
  const [ready, setReady] = useState(false);
  const handleReady = useCallback(() => setReady(true), []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      const ok = !mq.matches;
      setMotionOk(ok);
      if (!ok) setReady(false);
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  return (
    <div className={styles.zapBox} aria-hidden>
      {ready ? null : (
        <div className={styles.zapLayer}>
          <StaticZapMark />
        </div>
      )}
      {motionOk ? (
        <ZapLottie playToken={playToken} onReady={handleReady} />
      ) : null}
    </div>
  );
};

const ZapLottie = ({
  playToken,
  onReady,
}: {
  playToken: number;
  onReady: () => void;
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const ioRef = useRef<IntersectionObserver | null>(null);
  const deferRef = useRef<number | null>(null);
  const introStartedRef = useRef(false);
  const lastPlayedTokenRef = useRef(0);
  const playTokenRef = useRef(playToken);
  playTokenRef.current = playToken;
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  const play = useCallback((anim: AnimationItem) => {
    anim.resetSegments(true);
    anim.setSpeed(ZAP_PLAY_SPEED);
    anim.playSegments(ZAP_PLAY_SEGMENT, true);
  }, []);

  const onInstance = useCallback(
    (anim: AnimationItem) => {
      anim.addEventListener('complete', () => {
        anim.resetSegments(true);
        anim.goToAndStop(ZAP_REST_FRAME, true);
      });

      anim.addEventListener('DOMLoaded', () => {
        onReadyRef.current();
        const el = containerRef.current;
        if (!el) return;

        const token = playTokenRef.current;
        if (token && token !== lastPlayedTokenRef.current) {
          lastPlayedTokenRef.current = token;
          introStartedRef.current = true;
          play(anim);
          return;
        }

        const io = new IntersectionObserver(
          (entries) => {
            if (
              !entries.some((entry) => entry.isIntersecting) ||
              introStartedRef.current
            ) {
              return;
            }
            introStartedRef.current = true;
            io.disconnect();
            ioRef.current = null;
            deferRef.current = window.setTimeout(() => {
              deferRef.current = null;
              play(anim);
            }, ZAP_INTRO_DEFER_MS);
          },
          { threshold: 0.25 },
        );
        io.observe(el);
        ioRef.current = io;
      });
    },
    [play],
  );

  const animRef = useLottieAnimation(containerRef, {
    path: ZAP_LOTTIE_PATH,
    renderer: 'svg',
    loop: false,
    autoplay: false,
    initialSegment: ZAP_INITIAL_SEGMENT,
    onInstance,
  });

  useEffect(() => {
    if (!playToken) return;
    const anim = animRef.current;
    if (!anim?.isLoaded) return;
    if (playToken === lastPlayedTokenRef.current) return;
    lastPlayedTokenRef.current = playToken;
    introStartedRef.current = true;
    ioRef.current?.disconnect();
    ioRef.current = null;
    if (deferRef.current !== null) {
      window.clearTimeout(deferRef.current);
      deferRef.current = null;
    }
    play(anim);
  }, [playToken, animRef, play]);

  useEffect(
    () => () => {
      ioRef.current?.disconnect();
      ioRef.current = null;
      if (deferRef.current !== null) {
        window.clearTimeout(deferRef.current);
        deferRef.current = null;
      }
    },
    [],
  );

  return <div className={styles.zapLayer} ref={containerRef} />;
};
